import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { getSearchOfIndicators } from "../../../redux/slices/indicatorDisplay/SearchAndFilter/filterAndSearchThunk";
import { cleanSearch } from "../../../redux/slices/indicatorDisplay/SearchAndFilter/filterAndSearchSlice";
import { clearIndicators } from "../../../redux/slices/indicatorDisplay/indicator/indicatorSlice";
import { GetIndicatorsQuery } from "../../../redux/slices/indicatorDisplay/indicator/indicatorThunk";
import useUrlParams from "../../../hooks/useUrlParams";
import { scrollToElement } from "../../../utils/utils";
import { INDICATOR_TYPE_STRUCTURAL, SEARCH_MODE_EXPANDED, SEARCH_MODE_PREDICTIVE } from "../../../utils/const";
import { findIndicatorPath } from "../utils/filterTree";
import { getParamsToClean } from "../utils/filterParams";

// letras, números y . , (sin empezar con espacio)
const SEARCH_ALLOWED_CHARS = /^[A-Za-záéíóúüÁÉÍÓÚÜ0-9][A-Za-záéíóúüÁÉÍÓÚÜ0-9 .,]*$/;

interface UseIndicatorSearchParams {
    period: string;
    fetchIndicators: (query: Partial<GetIndicatorsQuery>) => void;
    resetDraft: () => void;
}

// Buscador de indicadores: predictivo (al escribir) y expandido (al presionar enter)
export const useIndicatorSearch = ({ period, fetchIndicators, resetDraft }: UseIndicatorSearchParams) => {
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { getParam, updateParams } = useUrlParams();
    const { rights, searchOfIndicators, loadingSearch, errorSearch } = useAppSelector((state) => state.indicator_display_filter);

    const [searchValue, setSearchValue] = useState<string>("");
    const [open, setOpen] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [cleanValue, setCleanValue] = useState<boolean>(false);
    const initialLoadDone = useRef(false);

    // el dropdown se abre si hay resultados, está cargando o hay error
    useEffect(() => {
        setOpen(Boolean(searchOfIndicators?.length || loadingSearch || errorSearch?.length));
    }, [searchOfIndicators, loadingSearch, errorSearch]);

    // Carga inicial: si la URL trae una búsqueda, se muestra en el input
    useEffect(() => {
        if (initialLoadDone.current) return;

        const params = new URLSearchParams(location.search);
        const search = params.get("search") || "";
        const searchMode = params.get("searchMode");
        const filterApplied = params.get("filterApplied");

        if (searchMode !== SEARCH_MODE_PREDICTIVE || filterApplied === "true") {
            if (search && searchMode === SEARCH_MODE_EXPANDED) {
                setSearchValue(search);
                setCleanValue(true);
            }
        } else {
            setSearchValue(search);
            setCleanValue(true);
        }

        initialLoadDone.current = true;
    }, [location.search]);

    useEffect(() => {
        return () => {
            dispatch(cleanSearch());
        };
    }, [dispatch]);

    // Vacía el input (también lo usa el filtro al aplicar estando en búsqueda predictiva)
    const clearSearchInput = () => {
        setSearchValue("");
        setCleanValue(false);
        dispatch(cleanSearch());
    };

    const onCleanValueSearch = () => {
        if (!getParam("search")?.length) return;

        clearSearchInput();
        dispatch(clearIndicators());
        updateParams({
            ...getParamsToClean("search"),
            ...getParamsToClean("all-filters"),
        });
        resetDraft();
    };

    const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);
        setError(false);

        if (SEARCH_ALLOWED_CHARS.test(value)) {
            if (value.length >= 3) {
                dispatch(getSearchOfIndicators({ search: value.trim(), startPeriod: Number(period) }));
            } else {
                dispatch(cleanSearch());
            }
            return;
        }

        setError(true);
        setOpen(false);
        if (value.length === 0 && getParam("search")) {
            onCleanValueSearch();
        }
    };

    /**
     * Selecciona un indicador del predictive search
     * */
    const selectSearch = ({ indicator, indicatorID, breadcrumbs }: { indicator: string; indicatorID: number; breadcrumbs: string; }) => {
        resetDraft();

        const { rightId, type, dimensionID, subdimensionID } = findIndicatorPath(rights, breadcrumbs);
        const isStructural = type === INDICATOR_TYPE_STRUCTURAL;

        updateParams({
            ...getParamsToClean("all-filters"),
            searchMode: SEARCH_MODE_PREDICTIVE,
            search: indicator,
            page: '1',
            indicatorID: indicatorID.toString(),
            type,
            ...(isStructural && {
                ...(dimensionID && { dimensionID }),
                ...(subdimensionID && { subdimensionID }),
            }),
        });
        fetchIndicators({
            rightsId: rightId ? [rightId] : [],
            indicatorsType: [type],
            indicatorIds: [indicatorID.toString()],
            limit: 1,
            ...(isStructural && dimensionID && subdimensionID && {
                dimension: [dimensionID],
                subdimension: [subdimensionID]
            }),
        });

        setSearchValue(indicator);
        setOpen(false);
        setCleanValue(true);
        dispatch(cleanSearch());
        scrollToElement("indicator-cards", 0, 1500);
    };

    /**
     * Inicializa búsqueda expandida al presionar enter en el input search
     * */
    const onSearchExpanded = (value: string) => {
        const valueToSearch = value.trim();
        if (value.length < 3) return;

        updateParams({
            ...getParamsToClean("all-filters"),
            searchMode: SEARCH_MODE_EXPANDED,
            search: valueToSearch,
            page: '1',
        });
        resetDraft();
        setOpen(false);
        setCleanValue(true);
        fetchIndicators({ search: valueToSearch });
        scrollToElement("indicator-cards", 0, 1500);
    };

    return {
        searchValue,
        open,
        setOpen,
        error,
        setError,
        cleanValue,
        onChangeSearch,
        selectSearch,
        onSearchExpanded,
        onCleanValueSearch,
        clearSearchInput,
    };
};
